/* Launch parameters (DESIGN47) — a deep link INTO the web app:

     /?project=<id>&open=poster|estimate|pricing|proposal|comments#token=<jwt>

   The mini app's «Open in the web app» builds it (the token is the same
   session; AuthContext adopts #token= and strips it). This module reads the
   query ONCE at load and strips it too, so a reload lands on the plain app.
   Everything here is optional: with no parameters nothing changes. */

export type LaunchPanel = 'poster' | 'estimate' | 'pricing' | 'proposal' | 'comments';

export interface Launch {
  project: string;
  open: LaunchPanel | null;
}

const PANELS: LaunchPanel[] = ['poster', 'estimate', 'pricing', 'proposal', 'comments'];
const ID_RE = /^[0-9a-zA-Z_-]{1,80}$/;

export function readLaunch(): Launch | null {
  if (typeof window === 'undefined') return null;
  // Other workspaces own their query parameters (for example Analytics' project slice).
  if (!['/', '/architect', '/architect/'].includes(window.location.pathname)) return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const project = (q.get('project') || '').trim();
    if (!ID_RE.test(project)) return null;
    const openRaw = (q.get('open') || '').trim() as LaunchPanel;
    const open = PANELS.includes(openRaw) ? openRaw : null;
    // Strip the query, keep the hash (AuthContext still needs #token=).
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    return { project, open };
  } catch {
    return null;
  }
}

let launch: Launch | null = readLaunch();
let projectTaken = false;
let panelTaken = false;

/** The launch target, if any — for routing decisions made BEFORE the editor
    mounts (the workspace chooser). Non-consuming. */
export function peekLaunch(): Launch | null {
  return launch;
}

/** The project to open — handed out ONCE, so leaving the editor and coming
    back does not replay the deep link. */
export function takeLaunchProject(): string | null {
  if (!launch || projectTaken) return null;
  projectTaken = true;
  return launch.project;
}

/** The panel to open — handed out ONCE, so a later project switch does not
    reopen it. */
export function takeLaunchPanel(projectId: string): LaunchPanel | null {
  if (!launch || panelTaken || launch.project !== projectId) return null;
  panelTaken = true;
  return launch.open;
}

/** Test hook. */
export function _setLaunchForTests(l: Launch | null): void {
  launch = l;
  projectTaken = false;
  panelTaken = false;
}
